<template>
  <div class="nuttrema-option-slider" :class="[`theme-${theme}`, { 'is-disabled': disabled }]">
    <resize-observer @notify="onResize"></resize-observer>
    <div v-if="title" class="nuttrema-option-slider__title">
      <p>{{ title }}</p>
    </div>
    <div class="nuttrema-option-slider__wrap" ref="wrap">
      <div class="nuttrema-option-slider__move" ref="move"></div>
      <slot></slot>
    </div>
    <div v-if="disclaimer" class="nuttrema-option-slider__disclaimer">
      <p>{{ disclaimer }}</p>
    </div>
  </div>
</template>

<script>
import { isArray, get, find } from 'lodash';
import { TimelineMax, Power4 } from 'gsap';
import { ResizeObserver } from 'vue-resize';

export default {
  name: 'nuttrema-option-slider-group',
  componentName: 'NuttremaOptionSliderGroup',
  components: {
    ResizeObserver,
  },
  props: {
    value: {
      required: true,
      default: null,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    title: {
      type: String,
      required: false,
      default: null,
    },
    disclaimer: {
      type: String,
      required: false,
      default: null,
    },
    theme: {
      type: String,
      required: false,
      default: 'default',
    },
  },
  data() {
    return {
      tl: null,
      timeout: false,
      delay: 250,
    };
  },
  methods: {
    getRelativeLeft(child) {
      const wrap = this.$refs.wrap;
      const wrapPos = wrap.getBoundingClientRect();
      const wrapStyle = window.getComputedStyle(wrap);
      const wrapBorder = String(wrapStyle.borderLeftWidth).replace('px', '');

      const childPos = child.getBoundingClientRect();
      const childStyle = window.getComputedStyle(child);
      const offsetLeft = String(childStyle.marginLeft).replace('px', '');

      return childPos.left - offsetLeft - wrapPos.left - wrapBorder;
    },
    onResize() {
      window.clearTimeout(this.timeout);
      this.timeout = window.setTimeout(() => {
        this.findAndSelect(this.value, 0);
      }, this.delay);
    },
    move(child, speed = 0.4) {
      const option = get(child, 'option', null);

      // console.log('move speed', child);

      if (option) {
        const ref = get(option, 'code', null);
        const color = get(option, 'data.color', '#fff');

        const el = this.$refs.move;

        const found = child.$refs[ref];
        const target = isArray(found) ? found[0] : (found || false);

        if (target) {
          const left = this.getRelativeLeft(target);
          this.tl.to(el, speed, {
            x: left,
            height: target.offsetHeight,
            width: target.offsetWidth,
            ease: Power4.easeInOut,
            backgroundColor: color,
          });
        }
      }
    },
    findAndSelect(value, speed) {
      const found = find(this.$children, children => Number(value) === Number(children.value));
      this.move(found, speed);
    },
  },
  watch: {
    value(val) {
      // console.log('changed value', val);
      this.$nextTick(() => {
        this.findAndSelect(val);
      });
    },
  },
  created() {
    this.$on('handleChange', (value) => {
      // console.log('handleChange', value);
      this.$emit('input', value);
    });
  },
  mounted() {
    this.tl = new TimelineMax();
    this.$nextTick(() => {
      this.findAndSelect(this.value);
      // window.addEventListener('resize', this.onResize);
    });
  },
  beforeDestroy() {
    // window.removeEventListener('resize', this.onResize);
  },
};
</script>

<style lang="scss" scoped>

.nuttrema-option-slider {
  flex-grow: 1;
  display: flex;
  flex-direction: column;

  .nuttrema-option-slider__wrap {
    display: flex;
    position: relative;
    border-radius: 12px;
    border: 2px solid #fff;
    justify-content: space-between;
  }

  .nuttrema-option-slider__option {
    z-index: 1;
    margin: 4px;
    flex-grow: 1;
    display: flex;
    padding: 0.5em;
    margin-right: 0;
    border-radius: 8px;
    align-items: center;
    flex-direction: column;
    justify-content: center;

    &:last-child {
      margin-right: 4px;
    }

    &:hover, &:focus, &:active {
      cursor: pointer;
      background-color: rgba(#fff, 0.25);
    }

    &.is-active {
      &:hover, &:focus, &:active {
        cursor: inherit;
        background-color: transparent;
      }
    }

    > * {
      text-align: center;
    }
  }

  .nuttrema-option-slider__disclaimer {
    flex-grow: 1;
    display: flex;
    margin: 0.25em 1em;
    flex-direction: column;
    > * {
      color: #fff;
      font-size: 0.85em;
      text-align: right;
    }
  }

  .nuttrema-option-slider__title {
    flex-grow: 1;
    display: flex;
    margin: 0.35em 1em;
    flex-direction: column;
    > * {
      color: #fff;
      text-align: left;
      font-size: 1.05em;
      font-weight: 500;
    }
  }

  .nuttrema-option-slider__move {
    z-index: 0;
    margin: 4px;
    top: 0; left: 0;
    position: absolute;
    border-radius: 8px;
    height: calc(100% - 8px); width: 12px;
    background-color: transparent;
  }

  &.theme-gray {
    .nuttrema-option-slider__wrap {
      border-color: #607d8b;
    }
    .nuttrema-option-slider__title, .nuttrema-option-slider__disclaimer {
      > * {
        color: #607d8b;
      }
    }
    &.is-disabled {
      .nuttrema-option-slider__move {
        background-color: #607d8b !important;
      }
    }
  }

  &.theme-topography, .nuttrema-theme-professional-topography & {
    .nuttrema-option-slider__wrap {
      border: 2px solid #dbdbdb;
      background-color: rgba(#fff, 0.75);
    }
    .nuttrema-option-slider__title, .nuttrema-option-slider__disclaimer {
      > * {
        color: #465568;
      }
    }
    &.is-disabled {
      .nuttrema-option-slider__move {
        background-color: #607d8b !important;
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/ui/switch/nuttrema-option-slider-group.vue